import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Brain,
  AlertTriangle,
  MessageSquare,
  Mic,
  Volume2,
  ExternalLink,
  Sparkles,
  MemoryStick,
  Cpu,
  Info,
  Copy,
  Check,
} from 'lucide-react';
import {
  mockNeMo26InstanceDetails,
  NeMoPretrainedModel,
  NeMoTaskType,
  getTrainingMethodLabel,
} from '@/mock/gpu-instance';
import { NeMoModelSelector, NeMoModelCard } from '../../components/nemo';

const NeMoModelsPage = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const [selectedModel, setSelectedModel] = useState<NeMoPretrainedModel | null>(null);
  const [modelCardOpen, setModelCardOpen] = useState(false);
  const [modelCardModel, setModelCardModel] = useState<NeMoPretrainedModel | null>(null);
  const [copiedPath, setCopiedPath] = useState<string | null>(null);

  const details = useMemo(() => mockNeMo26InstanceDetails(instanceId || ''), [instanceId]);

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedPath(text);
      setTimeout(() => setCopiedPath(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleViewModelCard = (model: NeMoPretrainedModel) => {
    setModelCardModel(model);
    setModelCardOpen(true);
  };

  const getTaskInfo = (task: NeMoTaskType) => {
    switch (task) {
      case 'llm':
        return {
          name: 'Language Model',
          icon: MessageSquare,
          color: 'text-green-500',
          bgColor: 'bg-green-500/10',
        };
      case 'asr':
        return {
          name: 'Speech Recognition',
          icon: Mic,
          color: 'text-blue-500',
          bgColor: 'bg-blue-500/10',
        };
      case 'tts':
        return {
          name: 'Text-to-Speech',
          icon: Volume2,
          color: 'text-purple-500',
          bgColor: 'bg-purple-500/10',
        };
    }
  };

  // Statistics
  const llmModels = details.pretrainedModels.filter(m => m.task === 'llm').length;
  const asrModels = details.pretrainedModels.filter(m => m.task === 'asr').length;
  const ttsModels = details.pretrainedModels.filter(m => m.task === 'tts').length;
  const loraSupportedModels = details.pretrainedModels.filter(m => m.supportedMethods.includes('lora')).length;

  return (
    <div className="container py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Brain className="h-6 w-6 text-green-500" />
            NeMo Model Catalog
          </h1>
          <p className="text-muted-foreground">
            Pretrained models from NVIDIA NGC for NeMo 2.6.1
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => window.open('https://catalog.ngc.nvidia.com/models?filters=&orderBy=weightPopularDESC&query=nemo', '_blank')}
        >
          <ExternalLink className="h-4 w-4 mr-2" />
          Open NGC Catalog
        </Button>
      </div>

      {/* Model Stats */}
      <div className="grid grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold">{details.pretrainedModels.length}</div>
            <div className="text-sm text-muted-foreground">Total Models</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-green-600">{llmModels}</div>
            <div className="text-sm text-muted-foreground">LLM</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{asrModels}</div>
            <div className="text-sm text-muted-foreground">ASR</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-purple-600">{ttsModels}</div>
            <div className="text-sm text-muted-foreground">TTS</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-yellow-600">{loraSupportedModels}</div>
            <div className="text-sm text-muted-foreground">LoRA Support</div>
          </CardContent>
        </Card>
      </div>

      {/* Model Browser */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Available Models</CardTitle>
          <CardDescription>Browse and select pretrained models for your experiments</CardDescription>
        </CardHeader>
        <CardContent>
          <NeMoModelSelector
            models={details.pretrainedModels}
            selectedModelId={selectedModel?.id}
            onSelectModel={setSelectedModel}
            onViewModelCard={handleViewModelCard}
          />
        </CardContent>
      </Card>

      {/* Selected Model Details */}
      {selectedModel && (
        <Card className="border-primary/50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${getTaskInfo(selectedModel.task).bgColor}`}>
                  {(() => {
                    const Icon = getTaskInfo(selectedModel.task).icon;
                    return <Icon className={`h-5 w-5 ${getTaskInfo(selectedModel.task).color}`} />;
                  })()}
                </div>
                <div>
                  <CardTitle>{selectedModel.displayName}</CardTitle>
                  <CardDescription>{selectedModel.description}</CardDescription>
                </div>
              </div>
              <div className="flex gap-2">
                {selectedModel.supportedMethods.includes('lora') && (
                  <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
                    <Sparkles className="h-3 w-3 mr-1" />
                    LoRA
                  </Badge>
                )}
                {selectedModel.supportedMethods.includes('qlora') && (
                  <Badge className="bg-purple-500/10 text-purple-600 border-purple-500/30">
                    <Sparkles className="h-3 w-3 mr-1" />
                    QLoRA
                  </Badge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Performance Metrics */}
            <div className="grid grid-cols-4 gap-4">
              <div className="p-4 bg-muted/50 rounded-lg text-center">
                <Cpu className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                <div className="text-xs text-muted-foreground">Parameters</div>
                <div className="text-xl font-bold">{selectedModel.parameters}</div>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg text-center">
                <MemoryStick className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                <div className="text-xs text-muted-foreground">VRAM (Full)</div>
                <div className="text-xl font-bold">{selectedModel.vramRequired} GB</div>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg text-center">
                <Sparkles className="h-5 w-5 mx-auto mb-1 text-green-500" />
                <div className="text-xs text-muted-foreground">VRAM (LoRA)</div>
                <div className="text-xl font-bold text-green-600">{selectedModel.vramLoRA} GB</div>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg text-center">
                <div className="text-xs text-muted-foreground mb-1">
                  {selectedModel.task === 'llm' ? 'Context Length' : 'Languages'}
                </div>
                <div className="text-xl font-bold font-mono">
                  {selectedModel.contextLength
                    ? `${(selectedModel.contextLength / 1000).toFixed(0)}K`
                    : selectedModel.languages?.join(', ').toUpperCase() || '-'}
                </div>
              </div>
            </div>

            {/* Model Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="text-sm text-muted-foreground mb-1">Family</div>
                  <code className="text-sm font-mono">{selectedModel.family}</code>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="text-sm text-muted-foreground mb-1">Task Type</div>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const info = getTaskInfo(selectedModel.task);
                      const Icon = info.icon;
                      return (
                        <>
                          <Icon className={`h-4 w-4 ${info.color}`} />
                          <span>{info.name}</span>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="text-sm text-muted-foreground mb-1">NGC Path</div>
                  <div className="flex items-center justify-between">
                    <code className="text-sm font-mono truncate">{selectedModel.ngcPath}</code>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0 ml-2"
                      onClick={() => handleCopy(selectedModel.ngcPath)}
                    >
                      {copiedPath === selectedModel.ngcPath ? (
                        <Check className="h-3 w-3" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="text-sm text-muted-foreground mb-2">Supported Methods</div>
                  <div className="flex flex-wrap gap-1">
                    {selectedModel.supportedMethods.map((method) => (
                      <Badge key={method} variant="secondary" className="text-xs">
                        {getTrainingMethodLabel(method)}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Features */}
            <div className="grid grid-cols-3 gap-3">
              <Card className={`${selectedModel.supportedMethods.includes('lora') ? 'border-green-500/50 bg-green-500/5' : 'opacity-50'}`}>
                <CardContent className="pt-4 text-center">
                  <Sparkles className={`h-6 w-6 mx-auto mb-2 ${selectedModel.supportedMethods.includes('lora') ? 'text-green-500' : 'text-gray-400'}`} />
                  <div className="font-medium text-sm">LoRA Fine-tuning</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {selectedModel.supportedMethods.includes('lora') ? 'Supported' : 'Not Supported'}
                  </div>
                </CardContent>
              </Card>
              <Card className={`${selectedModel.supportedMethods.includes('qlora') ? 'border-purple-500/50 bg-purple-500/5' : 'opacity-50'}`}>
                <CardContent className="pt-4 text-center">
                  <Sparkles className={`h-6 w-6 mx-auto mb-2 ${selectedModel.supportedMethods.includes('qlora') ? 'text-purple-500' : 'text-gray-400'}`} />
                  <div className="font-medium text-sm">QLoRA (4-bit)</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {selectedModel.supportedMethods.includes('qlora') ? 'Supported' : 'Not Supported'}
                  </div>
                </CardContent>
              </Card>
              <Card className="border-blue-500/50 bg-blue-500/5">
                <CardContent className="pt-4 text-center">
                  <Cpu className="h-6 w-6 mx-auto mb-2 text-blue-500" />
                  <div className="font-medium text-sm">Single GPU Training</div>
                  <div className="text-xs text-muted-foreground mt-1">Supported</div>
                </CardContent>
              </Card>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                className="flex-1"
                variant="outline"
                onClick={() => handleViewModelCard(selectedModel)}
              >
                <Info className="h-4 w-4 mr-2" />
                View Full Model Card
              </Button>
              {selectedModel.paperReference && (
                <Button
                  className="flex-1"
                  variant="outline"
                  onClick={() => window.open(selectedModel.paperReference, '_blank')}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View Paper
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Model Card Dialog */}
      <NeMoModelCard
        model={modelCardModel}
        open={modelCardOpen}
        onOpenChange={setModelCardOpen}
        onSelectModel={(model) => {
          setSelectedModel(model);
          setModelCardOpen(false);
        }}
      />

      {/* Demo Warning */}
      <Alert className="border-yellow-500/50 bg-yellow-500/5">
        <AlertTriangle className="h-4 w-4 text-yellow-600" />
        <AlertDescription className="text-yellow-700">
          <span className="font-medium">Demo Mode:</span> These models are from the NVIDIA NGC catalog.
          In production, models will be downloaded automatically when you create an experiment.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default NeMoModelsPage;
